import { useMemo, useState, useRef } from 'react';
import { transformPlanningData } from '../../utils/planningTransformer';
import { GROUPE_COLORS } from '../../constants/planningConstants';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import logoPreventis from '../../assets/logo-preventis.png';
import './PlanningTable.css';

const PlanningTable = ({ data }) => {
  const { personnes: allPersonnes, rows, totaux } = useMemo(() => transformPlanningData(data), [data]);
  
  // Filtrer les personnes pour exclure UNKNOWN
  const personnes = useMemo(() => {
    return allPersonnes.filter(p => p.key !== 'UNKNOWN_Non assigné');
  }, [allPersonnes]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const tableRef = useRef(null);

  // Calculer le nombre réel de personnes (exclure Direction General et Non assigné du comptage)
  const realPersonCount = useMemo(() => {
    return personnes.filter(p => 
      p.key !== 'UNKNOWN_Non assigné' && 
      p.key !== '798787607_Direction General'
    ).length;
  }, [personnes]);

  // Calculer le nombre unique de projets (sociétés) - exclure "Tâche non planifiée"
  const uniqueProjectCount = useMemo(() => {
    const projectNames = new Set();
    rows.forEach(row => {
      // Exclure les projets de la catégorie "Tâche non planifiée"
      if (row.type === 'tache-detail' && row.nomProjet && row.nomGroupe !== 'Tâche non planifiée') {
        projectNames.add(row.nomProjet);
      }
    });
    return projectNames.size;
  }, [rows]);

  const totalColumns = personnes.length + 2;
  const headerRightSpan = personnes.length >= 2 ? 2 : 1;
  const availableMiddleSpan = Math.max(totalColumns - 2 - headerRightSpan, 0);
  const headerTitleSpan = availableMiddleSpan;

  const formatPersonneName = (fullName) => {
    const parts = fullName.split(' ');
    if (parts.length >= 2) {
      const nom = parts[parts.length - 1];
      const prenom = parts.slice(0, -1).join(' ');
      return { prenom, nom };
    }
    return { prenom: fullName, nom: '' };
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const exportToPDF = async (excludeNonPlanifiees = false) => {
    if (!tableRef.current) return;
    
    setIsExporting(true);
    
    try {
      const tableElement = tableRef.current.querySelector('table');
      if (!tableElement) {
        throw new Error('Table non trouvée');
      }
      
      let clonedTable = tableElement;
      let shouldRestoreOriginal = false;
      
      // Si on doit exclure les tâches non planifiées, créer une copie filtrée
      if (excludeNonPlanifiees) {
        clonedTable = tableElement.cloneNode(true);
        
        // Supprimer les lignes de "Tâche non planifiée" (groupe + détails)
        const rows = clonedTable.querySelectorAll('tr');
        let skipNext = false;
        
        rows.forEach(row => {
          const groupeCell = row.querySelector('.cell-groupe');
          if (groupeCell && groupeCell.textContent.trim() === 'Tâche non planifiée') {
            row.remove();
            skipNext = true;
            return;
          }
          
          if (skipNext && row.classList.contains('row-tache-non-planifiee-style')) {
            row.remove();
            return;
          }
          
          if (skipNext && !row.classList.contains('row-tache-non-planifiee-style')) {
            skipNext = false;
          }
        });
        
        // Remplacer temporairement la table originale
        tableElement.style.display = 'none';
        tableElement.parentNode.insertBefore(clonedTable, tableElement);
        shouldRestoreOriginal = true;
      }
      
      const wrapper = tableRef.current;
      const originalOverflow = wrapper.style.overflow;
      const originalMaxHeight = wrapper.style.maxHeight;
      
      wrapper.style.overflow = 'visible';
      wrapper.style.maxHeight = 'none';
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const canvas = await html2canvas(clonedTable, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      // Restaurer les styles originaux
      wrapper.style.overflow = originalOverflow;
      wrapper.style.maxHeight = originalMaxHeight;
      
      // Restaurer la table originale si nécessaire
      if (shouldRestoreOriginal) {
        clonedTable.remove();
        tableElement.style.display = '';
      }

      const imgData = canvas.toDataURL('image/png');
      
      const imgWidthPx = canvas.width;
      const imgHeightPx = canvas.height;
      
      const pxToMm = 0.264583;
      const imgWidthMm = imgWidthPx * pxToMm;
      const imgHeightMm = imgHeightPx * pxToMm;
      
      const maxSinglePageHeightMm = 1000;
      
      // Si l'image tient sur une seule page
      if (imgHeightMm <= maxSinglePageHeightMm) {
        const pdf = new jsPDF({
          orientation: imgWidthMm > imgHeightMm ? 'landscape' : 'portrait',
          unit: 'mm',
          format: [imgWidthMm, imgHeightMm],
        });
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidthMm, imgHeightMm);
        
        const date = new Date().toISOString().split('T')[0];
        const fileName = excludeNonPlanifiees ? 
          `planification_planifiees_${date}.pdf` : 
          `planification_complete_${date}.pdf`;
        pdf.save(fileName);
      } else {
        const numberOfPages = Math.ceil(imgHeightMm / maxSinglePageHeightMm);
        const heightPerPageMm = imgHeightMm / numberOfPages;
        const heightPerPagePx = heightPerPageMm / pxToMm;
        
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: [imgWidthMm, heightPerPageMm],
        });
        
        let currentYPx = 0;
        
        for (let pageNumber = 0; pageNumber < numberOfPages; pageNumber++) {
          if (pageNumber > 0) {
            const remainingHeightPx = imgHeightPx - currentYPx;
            const heightForThisPagePx = Math.min(heightPerPagePx, remainingHeightPx);
            const heightForThisPageMm = heightForThisPagePx * pxToMm;
            
            pdf.addPage([imgWidthMm, heightForThisPageMm], 'landscape');
          }
          
          const remainingHeightPx = imgHeightPx - currentYPx;
          const heightForThisPagePx = Math.min(heightPerPagePx, remainingHeightPx);
          const heightForThisPageMm = heightForThisPagePx * pxToMm;
          
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = imgWidthPx;
          tempCanvas.height = heightForThisPagePx;
          
          const tempCtx = tempCanvas.getContext('2d');
          tempCtx.drawImage(
            canvas,
            0, currentYPx,
            imgWidthPx, heightForThisPagePx,
            0, 0,
            imgWidthPx, heightForThisPagePx
          );
          
          const tempImgData = tempCanvas.toDataURL('image/png');
          pdf.addImage(tempImgData, 'PNG', 0, 0, imgWidthMm, heightForThisPageMm);
          
          currentYPx += heightForThisPagePx;
        }
        
        const date = new Date().toISOString().split('T')[0];
        const fileName = excludeNonPlanifiees ? 
          `planification_planifiees_${date}.pdf` : 
          `planification_complete_${date}.pdf`;
        pdf.save(fileName);
      }
      
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Fonction utilitaire pour convertir hex en RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [224, 224, 224];
  };

  return (
    <div className={`planning-table-container ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className="table-header-actions">
        <div className="table-info">
          <span className="info-badge">
            <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            {realPersonCount} personne{realPersonCount > 1 ? 's' : ''}
          </span>
          <span className="info-badge">
            <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
            {uniqueProjectCount} projet{uniqueProjectCount > 1 ? 's' : ''}
          </span>
        </div>
        <div className="table-actions-buttons">
          <button 
            className="export-pdf-btn" 
            onClick={() => exportToPDF(false)}
            disabled={isExporting}
            title="Exporter PDF complet (avec tâches non planifiées)"
          >
            {isExporting ? (
              <>
                <svg className="btn-icon spinning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                </svg>
                Export en cours...
              </>
            ) : (
              <>
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="12" y1="18" x2="12" y2="12"></line>
                  <line x1="9" y1="15" x2="15" y2="15"></line>
                </svg>
                PDF Complet
              </>
            )}
          </button>
          <button 
            className="export-pdf-btn export-pdf-planifiees" 
            onClick={() => exportToPDF(true)}
            disabled={isExporting}
            title="Exporter PDF planifiées uniquement (sans tâches non planifiées)"
          >
            {isExporting ? (
              <>
                <svg className="btn-icon spinning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                </svg>
                Export en cours...
              </>
            ) : (
              <>
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <path d="M9 9l3 3L9 15"></path>
                </svg>
                PDF Planifiées
              </>
            )}
          </button>
          <button 
            className="fullscreen-btn" 
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Quitter le plein écran' : 'Afficher en plein écran'}
          >
            {isFullscreen ? (
              <>
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
                </svg>
                Quitter plein écran
              </>
            ) : (
              <>
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                </svg>
                Plein écran
              </>
            )}
          </button>
        </div>
      </div>

      <div className="table-wrapper" ref={tableRef}>
        <table className="planning-table">
          <colgroup>
            <col className="planning-col-societes" />
            <col className="planning-col-des" />
            {personnes.map((personne) => (
              <col key={`col-${personne.key}`} className="planning-col-personne" />
            ))}
          </colgroup>
          <thead>
            <tr className="document-header-row">
              <th className="document-header-logo-cell" colSpan={2}>
                <div className="document-header-logo-wrapper">
                  <img src={logoPreventis} alt="Preventis" className="document-header-logo" />
                </div>
              </th>
              {headerTitleSpan > 0 && (
                <th className="document-header-title-cell" colSpan={headerTitleSpan}>
                  <div className="document-header-title">PLANIFICATION PREVISIONNELLE</div>
                </th>
              )}
              <th className="document-header-meta-cell" colSpan={headerRightSpan}>
                <div className="document-header-meta-content">
                  <div className="document-header-meta-line">Code : FO-ETC-021</div>
                  <div className="document-header-meta-line">Révision : 02</div>
                </div>
              </th>
            </tr>
            <tr className="header-row">
              <th className="sticky-col col-societes">Sociétés</th>
              <th className="sticky-col-2 col-des">Dés.</th>
              {personnes.map((personne) => {
                const { prenom, nom } = formatPersonneName(personne.fullName);
                const isUnknown = personne.key === 'UNKNOWN_Non assigné';
                const personneStats = totaux[personne.key] || { 
                  totalJours: 0, 
                  sum_jourTravail: 0, 
                  nombreTaches: 0,
                  nombreTachesCompleted: 0,
                  pourcentageRealisation: 0
                };
                
                // Créer un tooltip détaillé
                const tooltipText = `${personne.fullName}
━━━━━━━━━━━━━━━━━━━━
📊 Total jours planifiés: ${personneStats.totalJours || 0}
✅ Total jours réalisés: ${personneStats.sum_jourTravail || 0}
━━━━━━━━━━━━━━━━━━━━
📋 Nombre de tâches: ${personneStats.nombreTaches || 0}
✓ Tâches complétées: ${personneStats.nombreTachesCompleted || 0}
📈 Taux de réalisation: ${(personneStats.pourcentageRealisation || 0).toFixed(1)}%`;
                
                return (
                  <th 
                    key={personne.key} 
                    className={`col-personne ${isUnknown ? 'col-personne-unknown' : ''}`}
                    title={tooltipText}
                  >
                    <div className="personne-name">
                      <span className="personne-prenom">{prenom}</span>
                      {nom && <span className="personne-nom">{nom}</span>}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              if (row.type === 'groupe') {
                return (
                  <tr key={`groupe-${index}`} className="row-groupe">
                    <td 
                      colSpan={personnes.length + 2} 
                      className="cell-groupe"
                      style={{ backgroundColor: GROUPE_COLORS[row.nomGroupe] || '#e0e0e0' }}
                    >
                      {row.nomGroupe}
                    </td>
                  </tr>
                );
              }

              if (row.type === 'tache-detail') {
                const isTacheNonPlanifiee = row.nomGroupe === 'Tâche non planifiée';
                
                // Déterminer le style de fond selon la catégorie
                let rowClassName = 'row-tache-detail';
                let cellClassName = 'cell-taches-detail';
                let itemClassName = 'tache-item-detail';
                
                // Appliquer des styles spécifiques selon la catégorie
                if (row.categorieTache === 'AUTRES' || row.categorieTache === 'Autres Missions') {
                  rowClassName += ' row-tache-standard-style';
                  itemClassName = 'tache-item-standard';
                } else if (isTacheNonPlanifiee) {
                  rowClassName += ' row-tache-non-planifiee-style';
                  itemClassName = 'tache-item-non-planifiee';
                } else {
                  rowClassName += ' row-tache-standard-style';
                  itemClassName = 'tache-item-standard';
                }
                
                return (
                  <tr key={`tache-detail-${index}`} className={rowClassName}>
                    {row.showProjectName && (
                      <td className="sticky-col cell-societe" rowSpan={row.rowspan}>
                        <div className={`project-name-container ${row.projectStatus && row.projectStatus.toLowerCase() === 'completed' ? 'completed-project' : ''}`}>
                          {row.projectStatus && row.projectStatus.toLowerCase() === 'completed' && (
                            <svg className="project-check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                          <span className={row.projectStatus && row.projectStatus.toLowerCase() === 'completed' ? 'project-completed' : ''}>
                            {row.nomProjet}
                          </span>
                        </div>
                      </td>
                    )}
                    <td className={`sticky-col-2 ${cellClassName}`}>
                      {row.taches.map((tacheObj, tacheIndex) => {
                        const tacheNom = typeof tacheObj === 'string' ? tacheObj : tacheObj.nomTache;
                        const tacheStatus = typeof tacheObj === 'object' ? tacheObj.status : null;
                        const isCloture = tacheStatus === 'Cloturé';
                        
                        return tacheNom && tacheNom.trim() !== '' ? (
                          <div key={tacheIndex} className={`${itemClassName} ${isCloture ? 'tache-cloturee' : ''}`}>
                            {isCloture && (
                              <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            )}
                            <span className="tache-text">{tacheNom}</span>
                          </div>
                        ) : (
                          <div key={tacheIndex} style={{ height: '0px' }}></div>
                        );
                      })}
                    </td>
                    {personnes.map((personne) => {
                      const personneData = row.personnesData[personne.key];
                      const isUnknown = personne.key === 'UNKNOWN_Non assigné';
                      const hasMultipleTasks = personneData?.tachesDetails && personneData.tachesDetails.length > 1;
                      
                      return (
                        <td key={personne.key} className={`cell-personne ${isUnknown ? 'cell-personne-unknown' : ''}`}>
                          {personneData && (personneData.sumJour > 0 || personneData.sumBillable_jour > 0) ? (
                            hasMultipleTasks ? (
                              // Si plusieurs tâches: afficher détails + somme
                              <div className="personne-details-horizontal">
                                {/* PARTIE GAUCHE: Détails de chaque tâche (petites boîtes) */}
                                <div className="taches-details-left">
                                  {personneData.tachesDetails.map((tache, idx) => (
                                    <div key={idx} className="tache-detail-row-horizontal">
                                      {isTacheNonPlanifiee ? (
                                        <div className="single-box-container-small">
                                          <div 
                                            className="box box-realise-small"
                                            title={`${tache.nomTache} - Réalisé: ${(parseFloat(tache.billableHours) / 7).toFixed(1)} jour(s)`}
                                          >
                                            <div className="box-label-small">R</div>
                                            <div className="box-value-small">{(parseFloat(tache.billableHours) / 7).toFixed(1)}</div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="dual-box-container-small">
                                          <div 
                                            className="box box-planifie-small"
                                            title={`${tache.nomTache} - Planifié: ${parseFloat(tache.nombreJour).toFixed(1)} jour(s)`}
                                          >
                                            <div className="box-label-small">P</div>
                                            <div className="box-value-small">{parseFloat(tache.nombreJour).toFixed(1)}</div>
                                          </div>
                                          <div 
                                            className="box box-realise-small"
                                            title={`${tache.nomTache} - Réalisé: ${(parseFloat(tache.billableHours) / 7).toFixed(1)} jour(s)`}
                                          >
                                            <div className="box-label-small">R</div>
                                            <div className="box-value-small">{(parseFloat(tache.billableHours) / 7).toFixed(1)}</div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                
                                {/* SÉPARATEUR VERTICAL */}
                                <div className="separator-vertical"></div>
                                
                                {/* PARTIE DROITE: Somme totale (grandes boîtes) */}
                                <div className="total-sum-right">
                                  {isTacheNonPlanifiee ? (
                                    <div className="single-box-container">
                                      <div 
                                        className="box box-realise box-total-sum"
                                        title={`Total Réalisé: ${personneData.sumBillable_jour || 0} jour(s) - ${personne.fullName}`}
                                      >
                                        <div className="box-label">R</div>
                                        <div className="box-value">{personneData.sumBillable_jour || 0}</div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="dual-box-container">
                                      <div 
                                        className="box box-planifie box-total-sum"
                                        title={`Total Planifié: ${personneData.sumJour || 0} jour(s) - ${personne.fullName}`}
                                      >
                                        <div className="box-label">P</div>
                                        <div className="box-value">{personneData.sumJour || 0}</div>
                                      </div>
                                      <div 
                                        className="box box-realise box-total-sum"
                                        title={`Total Réalisé: ${personneData.sumBillable_jour || 0} jour(s) - ${personne.fullName}`}
                                      >
                                        <div className="box-label">R</div>
                                        <div className="box-value">{personneData.sumBillable_jour || 0}</div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              // Si une seule tâche: afficher uniquement la somme (centrée)
                              <div className="personne-single-task">
                                {isTacheNonPlanifiee ? (
                                  <div className="single-box-container">
                                    <div 
                                      className="box box-realise box-total-sum"
                                      title={`Total Réalisé: ${personneData.sumBillable_jour || 0} jour(s) - ${personne.fullName}`}
                                    >
                                      <div className="box-label">R</div>
                                      <div className="box-value">{personneData.sumBillable_jour || 0}</div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="dual-box-container">
                                    <div 
                                      className="box box-planifie box-total-sum"
                                      title={`Total Planifié: ${personneData.sumJour || 0} jour(s) - ${personne.fullName}`}
                                    >
                                      <div className="box-label">P</div>
                                      <div className="box-value">{personneData.sumJour || 0}</div>
                                    </div>
                                    <div 
                                      className="box box-realise box-total-sum"
                                      title={`Total Réalisé: ${personneData.sumBillable_jour || 0} jour(s) - ${personne.fullName}`}
                                    >
                                      <div className="box-label">R</div>
                                      <div className="box-value">{personneData.sumBillable_jour || 0}</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          ) : ''}
                        </td>
                      );
                    })}
                  </tr>
                );
              }

              return null;
            })}

            <tr className="row-total">
              <td colSpan="2" className="cell-total-label">
                Nombre de jours total
              </td>
              {personnes.map((personne) => {
                const personneStats = totaux[personne.key] || { totalJours: 0, sum_jourTravail: 0 };
                const totalPlanifie = personneStats.totalJours || 0;
                const totalRealise = personneStats.sum_jourTravail || 0;
                const isUnknown = personne.key === 'UNKNOWN_Non assigné';
                return (
                  <td 
                    key={personne.key} 
                    className={`cell-total-value ${isUnknown ? 'cell-total-unknown' : ''}`}
                  >
                    <div className="dual-box-container">
                      <div 
                        className="box box-planifie box-total"
                        title={`Total planifié: ${totalPlanifie} jour(s) - ${personne.fullName}`}
                      >
                        <div className="box-label">P</div>
                        <div className="box-value">{totalPlanifie}</div>
                      </div>
                      <div 
                        className="box box-realise box-total"
                        title={`Total réalisé: ${totalRealise} jour(s) - ${personne.fullName}`}
                      >
                        <div className="box-label">R</div>
                        <div className="box-value">{totalRealise}</div>
                      </div>
                    </div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlanningTable;
